import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  FiBriefcase, FiPlus, FiSave, FiX, FiTrash2, FiEdit2, FiSearch,
  FiCalendar, FiInfo,
} from 'react-icons/fi';
import { delegationsAPI } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';
import DateField from '../../components/DateField';
import ConfirmDialog from '../../components/ConfirmDialog';
import './DelegationTab.scss';

// The form exchanges dates as 'YYYY-MM-DD', the format DateField speaks.
function toISODate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const EMPTY_FORM = {
  delegate: null,
  startDate: toISODate(new Date()),
  endDate: '',
  canInitiateContract: false,
  canAdvanceProcess: false,
  note: '',
};

/**
 * Search-as-you-type picker for the person receiving the delegation. Backed by
 * /delegations/candidates rather than the users API, because granting a
 * delegation is open to every user and must not need user-management rights.
 */
function DelegatePicker({ value, onChange, disabled }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    // Debounced so a fast typist fires one request, not one per keystroke.
    const timer = setTimeout(() => {
      delegationsAPI.candidates(q, ctrl.signal)
        .then((res) => {
          if (ctrl.signal.aborted) return;
          setResults(res.data?.users || []);
          setLoading(false);
        })
        .catch(() => { if (!ctrl.signal.aborted) setLoading(false); });
    }, 250);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [query]);

  if (value) {
    return (
      <div className="delegation-picker__selected">
        <span className="delegation-picker__avatar">
          {value.firstName?.[0]}{value.lastName?.[0]}
        </span>
        <span className="delegation-picker__selected-text">
          <strong>{value.firstName} {value.lastName}</strong>
          <span>{value.email}</span>
        </span>
        {!disabled && (
          <button
            type="button"
            className="delegation-picker__clear"
            onClick={() => onChange(null)}
            aria-label={t('common.clear', { defaultValue: 'Clear' })}
          >
            <FiX />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="delegation-picker" ref={wrapRef}>
      <div className="delegation-picker__input">
        <FiSearch />
        <input
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t('delegation.form.delegatePlaceholder')}
          autoComplete="off"
        />
      </div>
      {open && query.trim().length >= 2 && (
        <div className="delegation-picker__results">
          {loading ? (
            <div className="delegation-picker__empty">{t('common.loading')}</div>
          ) : results.length === 0 ? (
            <div className="delegation-picker__empty">{t('delegation.form.noCandidates')}</div>
          ) : (
            results.map((u) => (
              <button
                key={u._id}
                type="button"
                className="delegation-picker__result"
                onClick={() => { onChange(u); setOpen(false); setQuery(''); }}
              >
                <span className="delegation-picker__avatar">
                  {u.firstName?.[0]}{u.lastName?.[0]}
                </span>
                <span className="delegation-picker__result-text">
                  <strong>{u.firstName} {u.lastName}</strong>
                  <span>{u.email}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Profil > Vekalet — grant, adjust, and withdraw the delegations I give out.
 * "Bana Verilen Vekaletler" (the other direction) lives in the user menu.
 */
function DelegationTab() {
  const { t } = useTranslation();
  const [delegations, setDelegations] = useState([]);
  const [grantable, setGrantable] = useState({ canInitiateContract: false, canAdvanceProcess: false });
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = useCallback(async () => {
    try {
      const [mine, grants] = await Promise.all([
        delegationsAPI.mine(),
        delegationsAPI.grantable(),
      ]);
      setDelegations(mine.data?.delegations || []);
      setGrantable(grants.data?.scopes || {});
    } catch (err) {
      toast.error(err.response?.data?.error || t('delegation.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Deep link from the user menu / preferences quick action: /profile?tab=delegation&new=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === '1') setFormOpen(true);
  }, []);

  const canGrantAnything = grantable.canInitiateContract || grantable.canAdvanceProcess;

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (d) => {
    setEditingId(d._id);
    setForm({
      delegate: d.delegateId,
      startDate: toISODate(d.startDate),
      endDate: toISODate(d.endDate),
      canInitiateContract: !!d.scopes?.canInitiateContract,
      canAdvanceProcess: !!d.scopes?.canAdvanceProcess,
      note: d.note || '',
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!editingId && !form.delegate) {
      toast.error(t('delegation.form.delegateRequired'));
      return;
    }
    if (!form.startDate || !form.endDate) {
      toast.error(t('delegation.form.datesRequired'));
      return;
    }
    if (!form.canInitiateContract && !form.canAdvanceProcess) {
      toast.error(t('delegation.form.scopeRequired'));
      return;
    }

    const payload = {
      startDate: form.startDate,
      endDate: form.endDate,
      scopes: {
        canInitiateContract: form.canInitiateContract,
        canAdvanceProcess: form.canAdvanceProcess,
      },
      note: form.note,
    };

    setSaving(true);
    try {
      if (editingId) {
        await delegationsAPI.update(editingId, payload);
        toast.success(t('delegation.updated'));
      } else {
        await delegationsAPI.create({ ...payload, delegateId: form.delegate._id });
        toast.success(t('delegation.created'));
      }
      closeForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || t('delegation.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    const target = confirmRevoke;
    setConfirmRevoke(null);
    if (!target) return;
    try {
      await delegationsAPI.revoke(target._id);
      toast.success(t('delegation.revoked'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || t('delegation.revokeFailed'));
    }
  };

  const scopeLabels = (d) => [
    d.scopes?.canInitiateContract && t('delegation.scopes.initiate'),
    d.scopes?.canAdvanceProcess && t('delegation.scopes.advance'),
  ].filter(Boolean);

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <div className="settings-card__icon"><FiBriefcase /></div>
        <div className="settings-card__heading">
          <h3>{t('delegation.title')}</h3>
          <p>{t('delegation.description')}</p>
        </div>
        {!formOpen && canGrantAnything && (
          <button type="button" className="btn btn-primary delegation__new-btn" onClick={openCreate}>
            <FiPlus /> {t('delegation.new')}
          </button>
        )}
      </div>

      <div className="settings-card__body">
        {/* A user who holds neither authority has nothing to hand over — say so
            rather than presenting a form that can only be refused. */}
        {!canGrantAnything && !loading && (
          <div className="delegation__notice">
            <FiInfo />
            <span>{t('delegation.nothingToGrant')}</span>
          </div>
        )}

        {formOpen && (
          <form className="delegation-form" onSubmit={submit}>
            <div className="form-group">
              <label>{t('delegation.form.delegate')}</label>
              <DelegatePicker
                value={form.delegate}
                onChange={(u) => setForm({ ...form, delegate: u })}
                // The recipient is what a delegation *is* — changing it would be
                // a different grant, so an existing one is edited in place only
                // for its window and scopes.
                disabled={!!editingId}
              />
              {editingId && (
                <span className="settings-page__help-text">{t('delegation.form.delegateLocked')}</span>
              )}
            </div>

            <div className="settings-page__form-grid">
              <div className="form-group">
                <label>{t('delegation.form.startDate')}</label>
                <DateField
                  value={form.startDate}
                  onChange={(v) => setForm({ ...form, startDate: v })}
                  clearable={false}
                />
              </div>
              <div className="form-group">
                <label>{t('delegation.form.endDate')}</label>
                <DateField
                  value={form.endDate}
                  onChange={(v) => setForm({ ...form, endDate: v })}
                  min={form.startDate || undefined}
                  clearable={false}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('delegation.form.scopes')}</label>
              <div className="delegation-form__scopes">
                <label
                  className={`delegation-form__scope ${!grantable.canInitiateContract ? 'delegation-form__scope--disabled' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="delegation-form__scope-input"
                    checked={form.canInitiateContract}
                    disabled={!grantable.canInitiateContract}
                    onChange={(e) => setForm({ ...form, canInitiateContract: e.target.checked })}
                  />
                  <span className="delegation-form__scope-text">
                    <strong>{t('delegation.scopes.initiate')}</strong>
                    <span>
                      {grantable.canInitiateContract
                        ? t('delegation.scopes.initiateHint')
                        : t('delegation.scopes.notHeld')}
                    </span>
                  </span>
                </label>
                <label
                  className={`delegation-form__scope ${!grantable.canAdvanceProcess ? 'delegation-form__scope--disabled' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="delegation-form__scope-input"
                    checked={form.canAdvanceProcess}
                    disabled={!grantable.canAdvanceProcess}
                    onChange={(e) => setForm({ ...form, canAdvanceProcess: e.target.checked })}
                  />
                  <span className="delegation-form__scope-text">
                    <strong>{t('delegation.scopes.advance')}</strong>
                    <span>
                      {grantable.canAdvanceProcess
                        ? t('delegation.scopes.advanceHint')
                        : t('delegation.scopes.notHeld')}
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>{t('delegation.form.note')}</label>
              <input
                value={form.note}
                maxLength={500}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder={t('delegation.form.notePlaceholder')}
              />
            </div>

            <div className="settings-page__form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeForm}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                <FiSave /> {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="delegation__empty">{t('common.loading')}</div>
        ) : delegations.length === 0 ? (
          !formOpen && (
            <div className="delegation__empty">
              <FiBriefcase />
              <p>{t('delegation.empty')}</p>
            </div>
          )
        ) : (
          <div className="delegation__list">
            {delegations.map((d) => {
              const u = d.delegateId || {};
              const editable = d.status === 'active' || d.status === 'scheduled';
              return (
                <div key={d._id} className={`delegation-row delegation-row--${d.status}`}>
                  <span className="delegation-row__avatar">
                    {u.firstName?.[0]}{u.lastName?.[0]}
                  </span>
                  <div className="delegation-row__main">
                    <div className="delegation-row__name">
                      {u.firstName} {u.lastName}
                      <span className={`delegation-row__status delegation-row__status--${d.status}`}>
                        {t(`delegation.status.${d.status}`)}
                      </span>
                    </div>
                    <div className="delegation-row__meta">
                      <FiCalendar />
                      {formatDate(d.startDate)} — {formatDate(d.endDate)}
                    </div>
                    <div className="delegation-row__scopes">
                      {scopeLabels(d).map((label) => (
                        <span key={label} className="delegation-row__scope">{label}</span>
                      ))}
                    </div>
                    {d.note && <div className="delegation-row__note">{d.note}</div>}
                  </div>
                  <div className="delegation-row__actions">
                    {editable && (
                      <>
                        <button
                          type="button"
                          className="delegation-row__action"
                          onClick={() => openEdit(d)}
                          title={t('common.edit')}
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          className="delegation-row__action delegation-row__action--danger"
                          onClick={() => setConfirmRevoke(d)}
                          title={t('delegation.revoke')}
                        >
                          <FiTrash2 />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {confirmRevoke && (
        <ConfirmDialog
          open
          variant="danger"
          title={t('delegation.revokeTitle')}
          message={t('delegation.revokeConfirm', {
            name: `${confirmRevoke.delegateId?.firstName || ''} ${confirmRevoke.delegateId?.lastName || ''}`.trim(),
          })}
          confirmLabel={t('delegation.revoke')}
          onConfirm={revoke}
          onCancel={() => setConfirmRevoke(null)}
        />
      )}
    </div>
  );
}

export default DelegationTab;
